import {BaseLoader} from "./BaseLoader";
import {LoaderEvent} from "../events/LoaderEvent";
import {JSONLoader} from "./JSONLoader";
import {LoadingQueue} from "./LoadingQueue";
import {PIXILoader} from "./PIXILoader";
import {VideoLoader} from "./VideoLoader";
import {LayoutImporter} from "./LayoutImporter";
import {Loader} from "./Loader";
import {FontLoader} from "./FontLoader";
import * as log from "../utils/log"
import {Utils} from "../utils/Utils";

declare var DEBUG: boolean;

@log.decoratorConfig({isEnabled: DEBUG, isStylized: true})
export class LayoutLoader extends BaseLoader {
    warn

    private _jsonLoader: JSONLoader;
    private _resourcesLoader: LoadingQueue;
    private _pixiLoader: PIXILoader;
    private _videoAlternativeLoader: PIXILoader;
    private _videoLoaders: VideoLoader[];
    private _data: any;
    private _options: {
        json: PIXI.loaders.LoaderOptions,
        png: PIXI.loaders.LoaderOptions,
        jpg: PIXI.loaders.LoaderOptions
    }
    private _shouldUseResolutionForBitmapFonts: boolean


    constructor(src: string, cacheTimestamp?: string, prependURL?: string, id?: string,
                options?: {
                    json: PIXI.loaders.LoaderOptions,
                    png: PIXI.loaders.LoaderOptions,
                    jpg: PIXI.loaders.LoaderOptions
                },
                shouldUseResolutionForBitmapFonts: boolean = false) {
        super(src, cacheTimestamp, prependURL, id);

        this._options = options
        this._shouldUseResolutionForBitmapFonts = shouldUseResolutionForBitmapFonts
    }

    getData(): any {
        return this._data;
    }

    getTexture(name: string): PIXI.Texture {
        let texture = undefined;
        if (this._pixiLoader) {
            texture = this._pixiLoader.getTexture(name);
        }

        if (this._videoAlternativeLoader && !texture) {
            texture = this._videoAlternativeLoader.getTexture(name);
        }

        return texture;
    }

    getTextureSequence(sequenceName: string, extension: string,
                       texturesCount?: number, sequenceDigitsCount: number = 3): Array<PIXI.Texture> {
        if (this._pixiLoader) {
            return this._pixiLoader.getTextureSequence(sequenceName, extension, texturesCount, sequenceDigitsCount);
        }

        return undefined;
    }

    load() {
        if (this._state == BaseLoader.STATE_LOADING)
            return;
        else if (this._state == BaseLoader.STATE_LOADED) {
            // TODO: remove everithing with self use bind instead
            var self = this;
            setTimeout(function () {
                self.onLoaded();
            }, 0);
            return;
        }

        this._lastProgress = 0;
        this._state = BaseLoader.STATE_LOADING;
        this._jsonLoader.load();
    }

    stop() {
        if (this._state != BaseLoader.STATE_LOADING)
            return;

        this._state = BaseLoader.STATE_IDLE;

        this._jsonLoader.stop();

        if (this._resourcesLoader)
            this._resourcesLoader.stop();
    }

    dispose() {
        this._jsonLoader.removeEventListener(LoaderEvent.COMPLETE, this.onJSONLoaded, this);
        this._jsonLoader.removeEventListener(LoaderEvent.ERROR, this.onError, this);
        this._jsonLoader.dispose();

        if (this._resourcesLoader) {
            this._resourcesLoader.removeEventListener(LoaderEvent.COMPLETE,
                this.onResourcesLoaded, this);
            this._resourcesLoader.removeEventListener(LoaderEvent.PROGRESS, this.onProgress, this);
            this._resourcesLoader.removeEventListener(LoaderEvent.ERROR, this.onError, this);
            this._resourcesLoader.dispose();
        }

        if (this._videoLoaders) {
            for (let i = 0; i < this._videoLoaders.length; i++) {
                this._videoLoaders[i].removeEventListener(LoaderEvent.DECODE_ERROR, this.onVideoDecodeError, this);
            }
        }

        if (this._data) {
            this._data.mainContainer.destroy(true);
        }

        super.dispose();
    }

    protected getResponseType(): string {
        throw "This method is irrelevant for this type of loader.";
    }

    protected setUpRequest() {
        this._jsonLoader = new JSONLoader(this._src, this._cacheTimestamp, this._prependURL,
            "layoutJSONLoader_" + this._id);

        this._jsonLoader.addEventListener(LoaderEvent.COMPLETE, this.onJSONLoaded, this);
        this._jsonLoader.addEventListener(LoaderEvent.ERROR, this.onError, this);
    }

    protected parseLayout() {
        const importer = new LayoutImporter();
        this._data = importer.import(this._jsonLoader.getData().jsonContent,
            Loader.getLoader("layoutPIXILoader" + this._id) as PIXILoader);
    }

    protected getLoaderOptionsForFilename(fileName: string): PIXI.loaders.LoaderOptions {
        if (this._options === undefined) {
            return undefined
        }

        const extension = fileName.split(".").pop()
        const loaderOptions = this._options[extension]

        if (loaderOptions === undefined) {
            this.warn(`No loader options found for extension ${extension}.`)
        }

        return loaderOptions
    }

    private onJSONLoaded(event: LoaderEvent) {
        const resources: Array<string> = event.data.jsonContent.resources;
        const fontStyles: Array<string> = event.data.jsonContent.fontStyles;
        const fontNames: Array<string> = event.data.jsonContent.fontNames;
        const bitmapFonts: Array<string> = event.data.jsonContent.bitmapFonts;
        const videos: Array<string> = event.data.jsonContent.videos ? event.data.jsonContent.videos : [];

        if (resources.length == 0 && fontStyles.length == 0 && bitmapFonts.length == 0 && videos.length == 0) {
            this.parseLayout();
            this.onLoaded();

            return;
        }

        this._resourcesLoader = new LoadingQueue();

        let length = fontStyles.length;
        for (let i = 0; i < length; i++) {
            this._resourcesLoader.addLoader(new FontLoader(fontNames[i], fontStyles[i]));
        }

        if (resources.length != 0 || bitmapFonts.length != 0) {
            const lastSlashIndex = this._src.lastIndexOf("/");
            const prependURL: string = this._src.substring(0, lastSlashIndex);

            this._pixiLoader = new PIXILoader(this._cacheTimestamp, prependURL,
                "layoutPIXILoader" + this._id);

            this._resourcesLoader.addLoader(this._pixiLoader);
        }

        if (videos.length != 0) {
            this._videoLoaders = [];
            length = videos.length;
            for (let i = 0; i < length; i++) {
                const lastSlashIndex = this._src.lastIndexOf("/");
                const prependURL: string = this._src.substring(0, lastSlashIndex) + "/images/";

                const videoLoader = new VideoLoader(videos[i], this._cacheTimestamp, prependURL);
                videoLoader.addEventListener(LoaderEvent.DECODE_ERROR, this.onVideoDecodeError, this);

                this._videoLoaders.push(videoLoader);
                this._resourcesLoader.addLoader(videoLoader);
            }
        }

        length = resources.length;
        for (let i = 0; i < length; i++) {
            this._pixiLoader.addResource(resources[i], "images/" + resources[i],
                this.getLoaderOptionsForFilename(resources[i]));
        }

        length = bitmapFonts.length;
        for (let i = 0; i < length; i++) {
            let suffix = ""
            if (this._shouldUseResolutionForBitmapFonts) {
                suffix = Utils.getResolutionSuffix()
            }
            this._pixiLoader.addResource(bitmapFonts[i], `images/${bitmapFonts[i]}${suffix}.fnt`);
        }

        this._resourcesLoader.addEventListener(LoaderEvent.COMPLETE, this.onResourcesLoaded, this);
        this._resourcesLoader.addEventListener(LoaderEvent.PROGRESS, this.onProgress, this);
        this._resourcesLoader.addEventListener(LoaderEvent.ERROR, this.onError, this);

        this._resourcesLoader.load();
    }

    private onResourcesLoaded(event: LoaderEvent) {
        this.parseLayout();
        this.onLoaded();
    }

    protected onLoaded() {
        this._state = BaseLoader.STATE_LOADED;

        this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS,
            {progress: 1, progressChange: 1 - this._lastProgress}));

        this.dispatchEvent(new LoaderEvent(LoaderEvent.COMPLETE));
    }

    private onProgress(event: any) {
        var data =
            {
                progress: event.data.progress,
                progressChange: event.data.progress - this._lastProgress
            };

        this._lastProgress = event.data.progress;

        this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS, data));
    }

    protected onError(event: any) {
        this._state = BaseLoader.STATE_ERROR;
        this.dispatchEvent(new LoaderEvent(LoaderEvent.ERROR));
    }

    protected onVideoDecodeError() {
        const videoAlternative: string[] = this._jsonLoader.getData().jsonContent.videoAlternative;

        for (let i = 0; i < this._videoLoaders.length; i++) {
            const loader = this._videoLoaders[i];

            this._resourcesLoader.removeLoader(loader);
            loader.removeEventListener(LoaderEvent.DECODE_ERROR, this.onVideoDecodeError, this);
            loader.dispose();
        }

        this._videoLoaders.length = 0;

        if (videoAlternative && videoAlternative.length != 0) {
            const lastSlashIndex = this._src.lastIndexOf("/");
            const prependURL: string = this._src.substring(0, lastSlashIndex);

            this._videoAlternativeLoader = new PIXILoader(this._cacheTimestamp, prependURL,
                "videoAlternativeLoader" + this._id);

            for (let i = 0; i < videoAlternative.length; i++) {
                this._videoAlternativeLoader.addResource(videoAlternative[i], "images/" + videoAlternative[i],
                    this.getLoaderOptionsForFilename(videoAlternative[i]));
            }

            this._resourcesLoader.addLoader(this._videoAlternativeLoader);
        } else {
            throw new Error("Video alternative should always be provided");
        }
    }
}
